import React, { useCallback, useEffect, useState } from 'react';
import { getApiUrl } from '../config/api';
import { RationaleModal } from '../components/RationaleModal';
import { useToast } from '../components/Toast';

export type AdminPromptsTabProps = {
  fetchAdmin: (url: string, init?: RequestInit) => Promise<Response>;
};

type ModRow = {
  pk: string;
  sk: string;
  prompt_id: string;
  author_email: string;
  title: string;
  body: string;
  tags: string[];
  suggested_agent_ids: string[];
  mod_pk: string;
  created_at: string;
  review_note?: string;
  retired_by?: string;
  retired_at?: string;
  retirement_reason?: string;
};

const AdminPromptsTab: React.FC<AdminPromptsTabProps> = ({ fetchAdmin }) => {
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected' | 'retired'>('pending');
  const [rows, setRows] = useState<ModRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [acting, setActing] = useState<string | null>(null);
  const [pendingRetireRow, setPendingRetireRow] = useState<ModRow | null>(null);
  const [retireActing, setRetireActing] = useState<string | null>(null);
  const { show: showToast, toast: toastPortal } = useToast();

  const load = useCallback(async () => {
    setError(null);
    setRows([]);
    setLoading(true);
    try {
      const r = await fetchAdmin(`${getApiUrl('/admin/prompts')}?status=${encodeURIComponent(status)}`);
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const d = data.detail;
        setError(typeof d === 'string' ? d : `Load failed (${r.status})`);
        setRows([]);
        return;
      }
      if (data.table_configured === false) {
        setRows([]);
        setError('PROMPTS_TABLE_NAME is not configured on the API.');
        return;
      }
      setRows(Array.isArray(data.entries) ? data.entries : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fetchAdmin, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const moderate = async (row: ModRow, action: 'approve' | 'reject') => {
    setActing(row.sk);
    setError(null);
    try {
      const r = await fetchAdmin(getApiUrl('/admin/prompts/moderate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pk: row.pk,
          sk: row.sk,
          action,
          note: action === 'reject' ? note.trim() || undefined : undefined,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const d = data.detail;
        setError(typeof d === 'string' ? d : `Action failed (${r.status})`);
        return;
      }
      showToast(action === 'approve' ? 'Approved.' : 'Rejected.');
      setNote('');
      setRows(prev => prev.filter(r => r.sk !== row.sk));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActing(null);
    }
  };

  const handleRetireConfirm = async (rationale: string) => {
    const row = pendingRetireRow;
    if (!row) return;
    setPendingRetireRow(null);
    setRetireActing(row.sk);
    setError(null);
    try {
      const r = await fetchAdmin(getApiUrl('/admin/prompts/retire'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pk: row.pk, sk: row.sk, rationale }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const d = data.detail;
        setError(typeof d === 'string' ? d : `Retire failed (${r.status})`);
        return;
      }
      showToast('Prompt retired and removed from the global catalog.');
      setRows(prev => prev.filter(r => r.sk !== row.sk));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Retire failed');
    } finally {
      setRetireActing(null);
    }
  };

  const colSpan =
    status === 'pending' ? 6
    : status === 'approved' ? 6
    : status === 'retired' ? 8
    : 5;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        {(['pending', 'approved', 'rejected', 'retired'] as const).map(s => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded text-sm ${
              status === s ? 'bg-cyan-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-sm disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {status === 'pending' && (
        <label className="block text-xs text-gray-400 max-w-md">
          Optional reject note (applied to the next Reject click)
          <input
            value={note}
            onChange={e => setNote(e.target.value)}
            className="mt-1 w-full rounded border border-gray-600 bg-gray-900 px-2 py-1 text-sm text-gray-100"
            placeholder="Shown to author in My prompts"
          />
        </label>
      )}


      {error && <p className="text-sm text-red-400">{error}</p>}
      {toastPortal}

      <div className="rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-800/80 text-gray-300 border-b border-gray-700 sticky top-0">
              <tr>
                <th className="px-3 py-2 font-medium">Author</th>
                <th className="px-3 py-2 font-medium">Title</th>
                <th className="px-3 py-2 font-medium min-w-[200px]">Body</th>
                <th className="px-3 py-2 font-medium">Agents</th>
                <th className="px-3 py-2 font-medium">Created</th>
                {status === 'retired' && (
                  <>
                    <th className="px-3 py-2 font-medium">Retired by</th>
                    <th className="px-3 py-2 font-medium">Retired at</th>
                    <th className="px-3 py-2 font-medium min-w-[200px]">Rationale</th>
                  </>
                )}
                {(status === 'pending' || status === 'approved') && (
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                <tr>
                  <td colSpan={colSpan} className="px-3 py-10 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                      <svg className="animate-spin h-4 w-4 text-cyan-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={colSpan} className="px-3 py-8 text-center text-gray-500">
                    No rows in this queue.
                  </td>
                </tr>
              ) : (
                rows.map(row => (
                  <tr key={row.sk} className="hover:bg-gray-800/40 align-top">
                    <td className="px-3 py-2 font-mono text-xs text-cyan-100/90 break-all max-w-[140px]">
                      {row.author_email}
                    </td>
                    <td className="px-3 py-2 text-gray-200 max-w-[160px]">{row.title}</td>
                    <td className="px-3 py-2 text-gray-400 text-xs max-w-md whitespace-pre-wrap">{row.body}</td>
                    <td className="px-3 py-2 text-xs text-gray-400">{(row.suggested_agent_ids || []).join(', ')}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {row.created_at ? row.created_at.slice(0, 19).replace('T', ' ') : '—'}
                    </td>
                    {status === 'retired' && (
                      <>
                        <td className="px-3 py-2 text-xs text-gray-400 break-all max-w-[140px]">
                          {row.retired_by ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                          {row.retired_at ? row.retired_at.slice(0, 19).replace('T', ' ') : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-amber-200/80 whitespace-pre-wrap max-w-[200px]">
                          {row.retirement_reason ?? '—'}
                        </td>
                      </>
                    )}
                    {status === 'pending' && (
                      <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                        <button
                          type="button"
                          disabled={acting === row.sk}
                          onClick={() => void moderate(row, 'approve')}
                          className="px-2 py-1 rounded bg-emerald-800 hover:bg-emerald-700 text-xs text-white disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={acting === row.sk}
                          onClick={() => void moderate(row, 'reject')}
                          className="px-2 py-1 rounded bg-red-900/60 hover:bg-red-800 text-xs text-white disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </td>
                    )}
                    {status === 'approved' && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          disabled={retireActing === row.sk}
                          onClick={() => setPendingRetireRow(row)}
                          className="px-2 py-1 rounded bg-amber-800/70 hover:bg-amber-700 text-xs text-white disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {retireActing === row.sk ? 'Retiring…' : 'Retire'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <RationaleModal
        open={!!pendingRetireRow}
        title="Retire Prompt"
        description=""
        placeholder="Provide a required rationale explaining why this prompt is being retired."
        confirmLabel="Retire"
        onConfirm={rationale => void handleRetireConfirm(rationale)}
        onCancel={() => setPendingRetireRow(null)}
      />
    </div>
  );
};

export default AdminPromptsTab;
